import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ImageService } from 'src/app/shared/services/image.service';
import { SettingService } from 'src/app/shared/services/setting.service';
import { IOrder } from './order.service';
import { ITranslatableText } from './store.service';
import { ApiControllerService } from 'src/app/shared/services/api-controller.service';

export interface IDiscountDetails {
  discountTarget: string;
  discountType: string;
  discountValue: number;

  maxUseCount: number;
  maxUsePerOrderCount: number;


  isBuyoneGetone: boolean;
  isBuyoneGetDiscountValue: boolean;
  linkedItemID: string[];

  isBuyoneGetAnotherItem: boolean;
  isItemDiscountedFree: boolean;
  discountedItemListID: string[];

  autoApplyPromo: boolean;
  applyThroughPromoCode: boolean;
  applyFromOffers: boolean;
  applyFromCodeRemoveOffers: boolean;

  reusable: boolean;
  discountAfterTax: boolean;
}

export interface IRestrictions {
  amountMin: number;
  amountMax: number;
  //validInStore: boolean;
  //validOnline: boolean;
  validForPoS: boolean;

  isDiscountOnQuantityMatch: boolean;
  itemNeededForDiscount: number;

  // this is to know who can actually use it
  members: boolean;
  guests: boolean;
  membersAndGuests: boolean;

  //then who can see it
  visibilityRestrictions: IVisibilityRestrictions;
}

export interface IVisibilityRestrictions {
  membersOnly: boolean;
  guestsOnly: boolean;
  membersAndGuests: boolean;
}

export interface IValidity {
  activationDate: Date;
  noExpiration: boolean;
  expirationDate: Date;

  weeklyFormat: boolean;
  dailyValidity: IDailyValidity[];
}

export interface IDailyValidity {
  dayID: number[]; 
  dayName: string[];
  hoursRange: IHoursRange;
}

export interface IHoursRange {
  startHours: string;
  endHours: string;
}


export interface IVisualConfig {
  imgShortName: string;
  imgDisplayName: string;
  imgMobileShortName: string;
  imgMobileDisplayName: string;
}

export interface IBasePromotionRequest {
  merchantId: string;
  configId?: string;
}

export interface IPromotions {
  id: string
  merchantId: string;
  //promotionName: string;
  promotionName: ITranslatableText;
  promotionalCode: string;
  //promotionType: string;
  promotionType: ITranslatableText;
  promotionCategoryIndex: number;
  deactivatePromotion: boolean;
  platforms: string;
  discountDetails: IDiscountDetails;
  //audience: string;
  services: string[];
  locations: string[];
  restrictions: IRestrictions;
  //taC: string;
  taC: ITranslatableText;
  editingDates: IEditingDates;
  validity: IValidity;
  publicitySettings: IPublicitySettings;
  visualConfig: IVisualConfig;
  availableOnSMP: boolean;
  availableOnSMA: boolean;
  redeemableFromCallCenter: boolean;
  redeemableFromPOS: boolean;
}

export interface IPublicitySettings {
  sendViaEmail: boolean;
  sendViaNotficiation: boolean;
}

export interface IEditingDates {
  creationDate: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;
}


// this is only used within angular
export interface IDiscounts {
  discountPickup: number;
  discountDelivery: number;
  discountShipping: number;
  discountAfterTax: boolean;
  discountOnItem: boolean;
  itemIdDiscounted: string;
  discountOnDeliveryFee: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PromotionService {

  discounts: IDiscounts;

  private discountsSubject = new BehaviorSubject<IDiscounts>({
    discountPickup: 0,
    discountDelivery: 0,
    discountShipping: 0,
    discountAfterTax: false,
    discountOnItem: false,
    itemIdDiscounted: "",
    discountOnDeliveryFee: false
  });

  discounts$ = this.discountsSubject.asObservable();

  constructor(private http: HttpClient,
    private settingService: SettingService,
    private imageService: ImageService,
    private sanitizer: DomSanitizer,
    private apiController: ApiControllerService
  ) { }

  
  set setDiscounts(discountSetup: { type: string; discountNumber: number }){
    // discountSetup will be 2 values, 1 for the type (pickup, Delivery, etc.) and the other is the value
    const current = this.discountsSubject.value;
    let updatedDiscounts = { ...current };
    switch (discountSetup.type) {
      case 'pickup':
        updatedDiscounts.discountPickup = discountSetup.discountNumber;
        break;
      case 'delivery':
        updatedDiscounts.discountDelivery = discountSetup.discountNumber;
        break;
      case 'shipping':
        updatedDiscounts.discountShipping = discountSetup.discountNumber;
        break;
      default:
        console.warn('Unknown discount type:', discountSetup.type);
    }

    this.discountsSubject.next(updatedDiscounts);
    
  }

  set setDiscountsOptions(discountSetup: { type: string; discountOption }){
    // discountSetup will be 2 values, 1 for the type (pickup, Delivery, etc.) and the other is the value
    const current = this.discountsSubject.value;
    let updatedDiscounts = { ...current };
    switch (discountSetup.type) {
      case 'afterTax':
        updatedDiscounts.discountAfterTax = discountSetup.discountOption;
        break;
      case 'applyOnItem':
        updatedDiscounts.discountOnItem = discountSetup.discountOption;
        break;
      case 'itemDiscountedID':
        updatedDiscounts.itemIdDiscounted = discountSetup.discountOption;
        break;
      case 'applyOnDeliveryFee':
        updatedDiscounts.discountOnDeliveryFee = discountSetup.discountOption;
        break;
      default:
        console.warn('Unknown discount type:', discountSetup.type);
    }

    this.discountsSubject.next(updatedDiscounts);
    
  }
  

  get getDiscounts(): IDiscounts {
    return this.discountsSubject.value;
  }

  resetDiscounts() {
    const reset: IDiscounts = {
      discountPickup: 0,
      discountDelivery: 0,
      discountShipping: 0,
      discountAfterTax: false,
      discountOnItem: false,
      itemIdDiscounted: "",
      discountOnDeliveryFee: false
    };

    console.log("promotion discount reset");
    this.discountsSubject.next(reset);
  }

  getPromotions(promotionRequest: IBasePromotionRequest): Observable<IPromotions[]> {
    return Observable.create(o => {
      //this.http.post(this.settingService.apiUrl('Promotion/GetPromotions'), promotionRequest)
      this.apiController.post(this.settingService.apiUrl('Promotion/GetPromotions'), promotionRequest)
        .subscribe((data: any) => {
          o.next(data);
          o.complete();
        });
    })
  }



  // complimentary methods

  getImage(path: string): Observable<Blob> {
    return Observable.create(o => {
      //this.http.post(this.settingService.apiUrl('Menu/GetMenuItemImage'), {
      this.apiController.post(this.settingService.apiUrl('Menu/GetMenuItemImage'), {
        path: path
      }, { responseType: 'blob' })
        .pipe(finalize(() => {
          o.complete();
        }))
        .subscribe(
          result => o.next(result),
          res => {
            if (res.status != 400)
              // this.alert.showUnexpectedErrorMsg();
              o.error(res);
          }
        )
    });
  }

  fetchImage(ImageShortname) {
    return new Promise((resolve, reject) => {
      if (ImageShortname != null && ImageShortname != "") {
        this.getImage(ImageShortname).subscribe(data => {
          this.imageService.createImageFromBlob(data)
          .pipe(finalize(() => {
              
          }))
          .subscribe((buffer) => {
            
              //resolve(this.sanitizer.bypassSecurityTrustStyle(`url(${buffer})`));
              //resolve(this.sanitizer.bypassSecurityTrustStyle(`${buffer}`));
              resolve(buffer);
          });       
        }, error => {
        });
      } else {
        //resolve(this.sanitizer.bypassSecurityTrustStyle(`url(assets/imgs/menu/not-found.png)`));
        //resolve(this.sanitizer.bypassSecurityTrustStyle(`assets/imgs/menu/not-found.png`));
        resolve('');
      }
    });
  } 
}
